import React, { useMemo, useState, useEffect, useCallback, useRef } from "react";
import AwsEventDetails from "../Common/EventDetails/AwsEventDetails";
import Loader from "../Common/Loader/Loader";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import moment from "moment";
import { impactScore, getStatus, parseAck } from "../../utility/eventUtils";
import { mapAws, cleanTitle } from "../../utility/awsMapper";
import CheckIcon from "@mui/icons-material/Check";
import  "./AwsAlerts.css"
import {
  Box,
  Typography,
  TextField,
  InputAdornment,
  Chip,
  IconButton,
  Select,
  MenuItem,
  ListItemText,
  Button,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  Menu,
  CircularProgress,
} from "@mui/material";
import Paginator from "../Paginator/Paginator";
import SearchIcon from "@mui/icons-material/Search";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import RefreshIcon from "@mui/icons-material/Refresh";
import { KeyboardArrowDown, Square, Check } from "@mui/icons-material";
import UnfoldMoreIcon from "@mui/icons-material/UnfoldMore";
import { StyledRating } from "../ListView/ListView";
import { fetchAwsEvents } from "../../Services";
import CloudOffOutlined from "@mui/icons-material/CloudOffOutlined";
import CloseIcon from "@mui/icons-material/Close";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { Link } from "@mui/material";
import InfoIcon from "@mui/icons-material/Info";
import AcknowledgeDialog from "../Common/AcknowledgeDialog";
import * as XLSX from "xlsx";
import FileDownloadIcon from "@mui/icons-material/FileDownload";

const scopeSelectSx = {
  mr: 0.5,
  ml: 1,
  fontSize: "12px !important",
  color: "#000",
  height: 26,
  backgroundColor: "transparent",
  "& .MuiOutlinedInput-notchedOutline": { border: "none" },
  "&:hover .MuiOutlinedInput-notchedOutline": { border: "none" },
  "& .MuiSelect-select": {
    py: 0,
    pr: "24px",
    pl: "8px",
    display: "flex",
    alignItems: "center",
  },
  "& .MuiSvgIcon-root": { color: "#333", fontSize: 18 },
};

const scopeMenuProps = {
  PaperProps: {
    sx: {
      backgroundColor: "#F5F5F5",
      color: "#000",
      "& .MuiMenuItem-root": {
        fontSize: "11px",
        "&:hover": { backgroundColor: "#e0e0e0" },
        "&.Mui-selected": { backgroundColor: "#d0d0d0", color: "#333" },
      },
      maxWidth: 260,
    },
  },
  anchorOrigin: { vertical: "bottom", horizontal: "right" },
  transformOrigin: { vertical: "top", horizontal: "right" },
};

const headerSelectSx = {
  width: 160,
  height: 34,
  "& .MuiOutlinedInput-notchedOutline": { borderColor: "#E6E6E6" },
  "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#E6E6E6" },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#E6E6E6" },
  "& .MuiSelect-select": {
    display: "flex",
    alignItems: "center",
    height: "34px",
    py: 0,
  },
};

const InfoBanner = () => (
<Box
  role="note"
  className="bg-gray"
  sx={{
    display: "flex",
    alignItems: "center",
    columnGap: 1,
    rowGap: 0.5,
    p: 1,
    px: 1.25,
    mb: 1,
    borderRadius: 1,
    border: "1px solid rgba(0,0,0,0.1)",
    flexWrap: { xs: "wrap", sm: "nowrap" },
  }}
>
  <InfoIcon sx={{ fontSize: 18, color: "var(--theme)", flex: "0 0 auto" }} />

  <Typography
    component="span"
    variant="body2"
    className="text-body-color"
    sx={{
      display: "inline-flex",
      alignItems: "center",
      gap: 0.75,
      flexWrap: "wrap",
      fontSize: 13.5,
    }}
  >
    <span>Please reach out to</span>
    <Link
      href="https://autodesk.slack.com/app_redirect?channel=adsk-cloud-support"
      target="_blank"
      rel="noreferrer"
      underline="hover"
      className="text-primary"
      fontWeight={600}
    >
      #adsk-cloud-support
    </Link>
    <span>or email</span>
    <Link
      href="mailto:adsk.cloud.support@autodesk.com?subject=AWS%20Alerts%20Support"
      underline="hover"
      className="text-primary"
      fontWeight={600}
    >
      adsk.cloud.support@autodesk.com
    </Link>
    <span>for help.</span>
  </Typography>
</Box>
);

const AwsAlerts = ({
  userType,
  awsEvents,
  awsArchiveEvents,
  userEmail,
  awsEventsLoader,
  lastSyncedTime,
  awsProgress,
}) => {
  const navigate = useNavigate();
  const { releaseId: routeReleaseId } = useParams();

  // Tabs
  const [viewMode, setViewMode] = useState("active");
  const isArchivedView = viewMode === "archived";

  // Search & paging
  const [searchQuery, setSearchQuery] = useState("");
  const [searchScope, setSearchScope] = useState("Accounts");
  const [accountView, setAccountView] = useState("My Accounts");
  const ROWS_PER_PAGE_OPTS = [15, 50, "All"];
  const [rowsPerPage, setRowsPerPage] = useState(15);
  const [page, setPage] = useState(1);
  const [expandedAccount, setExpandedAccount] = useState(null);
  const [selectedRegion, setSelectedRegion] = useState(null);

  const [actByFilter, setActByFilter] = useState("All");
  const ACT_BY_FILTER_OPTIONS = ["All", "Current Week", "Current Month", "Next 3 Months", "Next 6 Months"];
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedReleaseId, setSelectedReleaseId] = useState(null);
  const [eventData, setEventData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [unauthorizedOpen, setUnauthorizedOpen] = useState(false);
  const refreshAttemptedRef = useRef(false);

  const [acctSort, setAcctSort] = useState({ key: "name", dir: "desc" });

  const { releaseId } = useParams();
  const loggedIn = sessionStorage.getItem("isLoggedIn") === "true";
  const location = useLocation();

  const eventsSource = viewMode === "active" ? awsEvents || {} : awsArchiveEvents || {};
  const { accounts, accountDetails, eventIndex } = useMemo(
    () => mapAws(eventsSource),
    [eventsSource]
  );

  const data = accounts;
  const currentAccountDetails = accountDetails;
  const localEventIndex = eventIndex;

  const SHOW_ALL = userType === "admin";

  const envById = useMemo(
    () => Object.fromEntries((data || []).map((a) => [a.id, a.env || ""])),
    [data]
  );
  const vendorById = useMemo(
    () =>
      Object.fromEntries((data || []).map((a) => [a.id, a.services?.[0] || ""])),
    [data]
  );

  // UI overrides
  const [statusOverrides, setStatusOverrides] = useState({}); // eventId -> "ACKNOWLEDGED"
  const [ackMetaByEvent, setAckMetaByEvent] = useState({}); // eventId -> { user, comment, date }
  const username = sessionStorage.getItem("username") || userEmail;

  useEffect(() => {
    const next = {};
    Object.values(localEventIndex || {}).forEach((ev) => {
      const acks = parseAck(ev.ack);
      if (acks.length) {
        const last = acks[acks.length - 1];
        next[ev.release_id] = {
          user: last.user || last.username || last.name || "",
          comment: last.comment || "",
          date: last.date || last.datetime || last.timestamp || "",
        };
      }
    });
    setAckMetaByEvent(next);
  }, [localEventIndex]);

  const buildEventData = useCallback(
    (id, sourceIndex = localEventIndex) => {
      const raw = sourceIndex?.[id];
      if (!raw) return null;

      const effectiveStatus = statusOverrides[id] || raw.status;

      const metaFromState = ackMetaByEvent[id];
      const acks = parseAck(raw.ack);
      const lastAck = acks.length ? acks[acks.length - 1] : null;

      const ackMeta =
        metaFromState ||
        (lastAck
          ? {
              user: lastAck.user || lastAck.username || lastAck.name || "",
              comment: lastAck.comment || "",
              date:
                lastAck.date || lastAck.datetime || lastAck.timestamp || "",
            }
          : undefined);

      const acknowledged =
        effectiveStatus === "ACKNOWLEDGED" || (acks && acks.length > 0);

      return {
        ...raw,
        status: effectiveStatus,
        acknowledged,
        ackMeta,
      };
    },
    [localEventIndex, statusOverrides, ackMetaByEvent]
  );

  const normalize = (v) => String(v ?? "").toLowerCase();

  // Search helper
  const eventMatchesQuery = useCallback((item, q, extras = {}) => {
    if (!q) return true;
    const nq = normalize(q);

    const effectiveStatus = extras.effectiveStatus || item.status;
    const statusDisplay = getStatus(effectiveStatus)?.display ?? "";
    const impacted = Array.isArray(item.impacted_services)
      ? item.impacted_services.join(" ")
      : item.impacted_services;

    const fields = [
      item.eventId,
      item.title,
      cleanTitle(item.title),
      item.impact,
      item.actionRequired,
      effectiveStatus,
      statusDisplay,
      item.region,
      item.category,
      item.summary,
      item.act_by,
      item.planned_start_date,
      item.planned_end_date,
      item.last_updated,
      item.release_url,
      impacted,
      extras.environment,
      extras.vendor_service,
    ];

    return fields.map((f) => normalize(f)).some((f) => f && f.includes(nq));
  }, []);

  const isInActByFilter = useCallback(
    (dateStr) => {
      if (!dateStr || actByFilter === "All") return true;
      const d = moment(dateStr);
      if (!d.isValid()) return false;

      const today = moment();
      switch (actByFilter) {
        case "Current Week":
          return d.isSame(today, "week");
        case "Current Month":
          return d.isSame(today, "month");
        case "Next 3 Months": {
          const end = moment().add(3, "months").endOf("day");
          return d.isSameOrAfter(today.startOf("day")) && d.isSameOrBefore(end);
        }
        case "Next 6 Months": {
          const end = moment().add(6, "months").endOf("day");
          return d.isSameOrAfter(today.startOf("day")) && d.isSameOrBefore(end);
        }
        default:
          return true;
      }
    },
    [actByFilter]
  );

  const baseAccounts = useMemo(() => {
    if (!data) return [];
    return accountView === "My Accounts"
      ? data.filter((a) => a?.self_account === true)
      : data;
  }, [data, accountView]);

  const hasEvents = baseAccounts && baseAccounts.length > 0;

  const filteredAccounts = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q && actByFilter === "All") return baseAccounts;

    const searchAccounts = searchScope === "Accounts";
    const searchEvents = searchScope === "Events";

    return baseAccounts.filter((a) => {
      if (searchAccounts) {
        const inName = a.name?.toLowerCase().includes(q);
        const inId = a.id?.toLowerCase().includes(q);
        const inEnv = a.env?.toLowerCase().includes(q);
        const inSvc = (a.services || []).some((s) =>
          String(s).toLowerCase().includes(q)
        );
        return q ? (inName || inId || inEnv || inSvc) : true;
      }
      if (searchEvents) {
        const events = currentAccountDetails[a.id] || [];
        return events.some((ev) => {
          const matchesQuery = q
            ? eventMatchesQuery(ev, q, {
                environment: envById[a.id],
                vendor_service: vendorById[a.id],
                effectiveStatus: statusOverrides[ev.eventId],
              })
            : true;
          const matchesActBy = isInActByFilter(ev.act_by);
          return matchesQuery && matchesActBy;
        });
      }
      const events = currentAccountDetails[a.id] || [];
      return events.some((ev) => isInActByFilter(ev.act_by));
    });
  }, [
    baseAccounts,
    currentAccountDetails,
    envById,
    vendorById,
    eventMatchesQuery,
    searchQuery,
    searchScope,
    statusOverrides,
    actByFilter,
    isInActByFilter,
  ]);

  // ----- Sorting helpers -----
  const collator = useMemo(
    () => new Intl.Collator(undefined, { numeric: true, sensitivity: "base" }),
    []
  );
  const naturalCompare = useCallback(
    (a, b) => collator.compare(String(a ?? ""), String(b ?? "")),
    [collator]
  );

  const toNumberOrNull = (val) => {
    if (val == null) return null;
    const num = Number(String(val).replace(/[^\d.-]/g, ""));
    return Number.isFinite(num) ? num : null;
  };

  // Count events for an account after filters/search
  const filteredEventCountForAccount = useCallback(
    (accountId) => {
      const events = currentAccountDetails[accountId] || [];
      const q = searchQuery.trim().toLowerCase();

      let filtered = events.filter((ev) => isInActByFilter(ev.act_by));

      if (searchScope === "Events" && q) {
        filtered = filtered.filter((ev) =>
          eventMatchesQuery(ev, q, {
            environment: envById[accountId],
            vendor_service: vendorById[accountId],
            effectiveStatus: statusOverrides[ev.eventId],
          })
        );
      }

      return filtered.length;
    },
    [
      currentAccountDetails,
      isInActByFilter,
      searchQuery,
      searchScope,
      envById,
      vendorById,
      statusOverrides,
      eventMatchesQuery,
    ]
  );

  const handleHeaderClick = (key) => {
    setAcctSort((prev) => {
      if (prev.key === key) {
        const nextDir = prev.dir === "asc" ? "desc" : "asc";
        return { key, dir: nextDir };
      }
      return { key, dir: "asc" };
    });
    setPage(1);
  };

  const handleHeaderKeyDown = (e, key) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      handleHeaderClick(key);
    }
  };

  const SortLabel = ({ active, dir, children }) => (
    <Box
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 0.5,
        cursor: "pointer",
        userSelect: "none",
        fontWeight: active ? 700 : 600,
      }}
    >
      <span>{children}</span>
      {active ? (
        <span style={{ fontSize: 11, lineHeight: 1 }}>{dir === "asc" ? "▲" : "▼"}</span>
      ) : (
        <UnfoldMoreIcon sx={{ fontSize: 16, opacity: 0.5 }} />
      )}
    </Box>
  );

  const sortedAccounts = useMemo(() => {
    if (!filteredAccounts?.length || !acctSort.key || !acctSort.dir)
      return filteredAccounts;

    const copy = [...filteredAccounts];
    copy.sort((a, b) => {
      let cmp = 0;
      switch (acctSort.key) {
        case "name":
          cmp = naturalCompare(a.name, b.name);
          break;
        case "id": {
          const an = toNumberOrNull(a.id);
          const bn = toNumberOrNull(b.id);
          cmp = an != null && bn != null ? an - bn : naturalCompare(a.id, b.id);
          break;
        }
        case "env":
          cmp = naturalCompare(a.env, b.env);
          break;
        case "total": {
          const ta = filteredEventCountForAccount(a.id);
          const tb = filteredEventCountForAccount(b.id);
          cmp = ta - tb;
          break;
        }
        default:
          cmp = 0;
      }
      return acctSort.dir === "asc" ? cmp : -cmp;
    });

    return copy;
  }, [filteredAccounts, acctSort, naturalCompare, filteredEventCountForAccount]);

  // ---------- Pagination uses sorted accounts ----------
  const totalPages =
    rowsPerPage === "All"
      ? 1
      : Math.max(1, Math.ceil((sortedAccounts?.length || 0) / rowsPerPage));

  const currentPageAccounts =
    rowsPerPage === "All"
      ? sortedAccounts
      : sortedAccounts.slice((page - 1) * rowsPerPage, page * rowsPerPage);

  // ---------- Regions & expanded section ----------
  const getRegionsForAccount = useCallback(
    (accountId, { matchQuery = false } = {}) => {
      const all = currentAccountDetails[accountId] || [];
      let list = all;

      if (matchQuery && searchScope === "Events") {
        const q = searchQuery.trim().toLowerCase();
        if (q) {
          list = list.filter((ev) =>
            eventMatchesQuery(ev, q, {
              environment: envById[accountId],
              vendor_service: vendorById[accountId],
              effectiveStatus: statusOverrides[ev.eventId],
            })
          );
        }
      }

      list = list.filter((ev) => isInActByFilter(ev.act_by));

      const regions = list.map((item) => item.region);
      return [...new Set(regions)].filter(Boolean);
    },
    [
      currentAccountDetails,
      searchScope,
      searchQuery,
      eventMatchesQuery,
      envById,
      vendorById,
      statusOverrides,
      isInActByFilter,
    ]
  );

  const expandedEvents = useMemo(() => {
    if (!expandedAccount || !currentAccountDetails[expandedAccount]) return [];
    let list = currentAccountDetails[expandedAccount];
    if (selectedRegion) list = list.filter((item) => item.region === selectedRegion);

    if (searchScope === "Events") {
      const q = searchQuery.trim().toLowerCase();
      if (q) {
        list = list.filter((ev) =>
          eventMatchesQuery(ev, q, {
            environment: envById[expandedAccount],
            vendor_service: vendorById[expandedAccount],
            effectiveStatus: statusOverrides[ev.eventId],
          })
        );
      }
    }

    list = list.filter((ev) => isInActByFilter(ev.act_by));

    return list;
  }, [
    expandedAccount,
    currentAccountDetails,
    selectedRegion,
    searchScope,
    searchQuery,
    eventMatchesQuery,
    envById,
    vendorById,
    statusOverrides,
    isInActByFilter,
  ]);

  const fmt = (d) => {
    if (!d) return "N/A";
    const x = new Date(d);
    return isNaN(x)
      ? "N/A"
      : x.toLocaleDateString("en-US", {
          year: "numeric",
          month: "short",
          day: "numeric",
        });
  };

  const getImpactedTotal = (impacted_services) => {
    if (!impacted_services) return 0;

    if (Array.isArray(impacted_services)) {
      return impacted_services.filter(Boolean).length;
    }

    return String(impacted_services)
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean).length;
  };

  const getImpactProgress = (item) => {
    const total = getImpactedTotal(item.impacted_services);
    const completed = Number(item.impacted_completed);
    const show = Number.isFinite(completed) && completed !== -1 && total > 0;

    const safeCompleted = show ? Math.max(0, Math.min(completed, total)) : null;

    return { show, completed: safeCompleted, total };
  };


  const expandedAccountRegions = useMemo(() => {
    if (!expandedAccount || !currentAccountDetails[expandedAccount]) return [];
    return getRegionsForAccount(expandedAccount, { matchQuery: true });
  }, [expandedAccount, currentAccountDetails, getRegionsForAccount]);

  useEffect(() => setPage(1), [searchQuery, rowsPerPage, searchScope]);
  useEffect(() => setPage(1), [actByFilter]);

  useEffect(() => {
    setPage(1);
    setExpandedAccount(null);
    setSelectedRegion(null);
  }, [accountView]);

  useEffect(() => {
    setPage(1);
    setExpandedAccount(null);
    setSelectedRegion(null);
  }, [viewMode]);

  const openRelease = useCallback(
    (id) => {
      setSelectedReleaseId(id);
      const enriched = buildEventData(id);
      setEventData(enriched);
      setIsModalOpen(true);
      navigate(`/aws/${id}`, { replace: false });
    },
    [navigate, buildEventData]
  );

  const closeRelease = useCallback(() => {
    setIsModalOpen(false);
    setSelectedReleaseId(null);
    setEventData(null);
    setLoading(false);
    navigate("/aws", { replace: true });
  }, [navigate]);

  useEffect(() => {
    if (!expandedAccount) return;
    const regions = getRegionsForAccount(expandedAccount, { matchQuery: true });
    if (!regions.includes(selectedRegion)) {
      setSelectedRegion(regions[0] || null);
    }
  }, [expandedAccount, getRegionsForAccount, selectedRegion]);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      if (!routeReleaseId) return;

      setLoading(false);
      if (awsEventsLoader) return;

      const local = localEventIndex[routeReleaseId];
      if (local) {
        setSelectedReleaseId(routeReleaseId);
        setEventData(buildEventData(routeReleaseId));
        setIsModalOpen(true);
        return;
      }

      let refreshedIndex = null;
      if (!refreshAttemptedRef.current) {
        refreshAttemptedRef.current = true;
        try {
          setIsModalOpen(true);
          setLoading(true);
          const res = await fetchAwsEvents(
            userEmail,
            viewMode === "archived" ? "archive" : "active",
            200
          );
          if (!cancelled) {
            const fresh = res?.data?.data || {};
            refreshedIndex = mapAws(fresh).eventIndex;
          }
        } catch (e) {
          console.error("AWS Alerts refresh failed:", e);
        }
      }

      if (!cancelled && refreshedIndex?.[routeReleaseId]) {
        setSelectedReleaseId(routeReleaseId);
        const enriched = buildEventData(routeReleaseId, refreshedIndex);
        setEventData(enriched);
        setIsModalOpen(true);
        return;
      }

      setUnauthorizedOpen(true);
      setIsModalOpen(false);
      setSelectedReleaseId(null);
      setEventData(null);
      setLoading(false);
      navigate("/aws", { replace: true });
    })();

    return () => {
      cancelled = true;
      refreshAttemptedRef.current = false;
    };
  }, [
    routeReleaseId,
    awsEventsLoader,
    localEventIndex,
    userEmail,
    navigate,
    viewMode,
    buildEventData,
  ]);

  useEffect(() => {
    if (!routeReleaseId) {
      setIsModalOpen(false);
      setSelectedReleaseId(null);
      setEventData(null);
      setLoading(false);
    }
  }, [routeReleaseId]);

  useEffect(() => {
    if (!loggedIn && location.pathname.startsWith("/aws")) {
      sessionStorage.setItem("path", location.pathname.substring(1));
      sessionStorage.setItem("directLogin", true);
      console.log(location.pathname);
      navigate("/login");
    } else if (loggedIn && location.pathname.startsWith("/aws")) {
      console.log(location.pathname);
      sessionStorage.removeItem("path");
      sessionStorage.removeItem("directLogin");
      sessionStorage.removeItem("postBootTarget");
    }
  }, [loggedIn]);

  const [actionAnchorEl, setActionAnchorEl] = useState(null);
  const [actionMenuEventId, setActionMenuEventId] = useState(null);
  const [actionSelections, setActionSelections] = useState({});
  const [ackOpen, setAckOpen] = useState(false);
  const [ackEventId, setAckEventId] = useState(null);
  const [ackComment, setAckComment] = useState("");
  const [ackComments, setAckComments] = useState({});

  const openActionMenu = (e, eventId) => {
    setActionAnchorEl(e.currentTarget);
    setActionMenuEventId(eventId);
  };
  const closeActionMenu = () => {
    setActionAnchorEl(null);
    setActionMenuEventId(null);
  };

  const selectAction = (option) => {
    if (!actionMenuEventId) return;
    if (option === "Acknowledgement") {
      setAckEventId(actionMenuEventId);
      setAckComment(ackComments[actionMenuEventId] || "");
      setAckOpen(true);
      closeActionMenu();
    } else {
      setActionSelections((prev) => ({ ...prev, [actionMenuEventId]: option }));
      closeActionMenu();
    }
  };
  const handleAckClose = () => {
    setAckOpen(false);
    setAckEventId(null);
    setAckComment("");
  };

  const handleAckSuccess = ({ eventId, comment, date, user }) => {
    setActionSelections((prev) => ({
      ...prev,
      [eventId]: "Acknowledgement",
    }));
    setAckComments((prev) => ({ ...prev, [eventId]: comment }));
    setStatusOverrides((prev) => ({ ...prev, [eventId]: "ACKNOWLEDGED" }));

    setAckMetaByEvent((prev) => ({
      ...prev,
      [eventId]: {
        user: user || username,
        comment,
        date,
      },
    }));

    setEventData((prev) => {
      if (!prev || prev.eventId !== eventId) return prev;
      return {
        ...prev,
        status: "ACKNOWLEDGED",
        acknowledged: true,
        ackMeta: {
          user: user || username,
          comment,
          date,
        },
      };
    });

    handleAckClose();
  };

  if (awsEventsLoader) return <Loader />;

  const hasEventsToShow = hasEvents;

  const handleExport = () => {
    try {
      const activeMapped = mapAws(awsEvents || {});
      const archiveMapped = mapAws(awsArchiveEvents || {});

      const allAccounts = [
        ...(activeMapped.accounts || []),
        ...(archiveMapped.accounts || []),
      ];
      const allDetails = {
        ...(activeMapped.accountDetails || {}),
        ...(archiveMapped.accountDetails || {}),
      };

      const filteredAccountsForExport =
        accountView === "My Accounts"
          ? allAccounts.filter((a) => a?.self_account === true)
          : allAccounts;

      const rows = [];

      filteredAccountsForExport.forEach((account) => {
        let events = (allDetails[account.id] || []).filter((ev) =>
          isInActByFilter(ev.act_by)
        );

        const q = searchQuery.trim().toLowerCase();
        const shouldFilterByQuery = searchScope === "Events" && q;
        if (shouldFilterByQuery) {
          events = events.filter((ev) =>
            eventMatchesQuery(ev, q, {
              environment: account.env,
              vendor_service: (account.services || [])[0] || "",
              effectiveStatus: statusOverrides[ev.eventId],
            })
          );
        }

        events.forEach((ev) => {
          rows.push({
            "Account Name": account.name,
            "Account ID": account.id,
            Environment: account.env,
            "Business Services": (account.services || []).join(", "),
            "Event ID": ev.eventId,
            Title: cleanTitle(ev.title),
            Impact: ev.impact,
            "Action Required": ev.actionRequired,
            "Start Date": ev.planned_start_date,
            "End Date": ev.planned_end_date,
            "Act By": ev.act_by,
            Status: getStatus(ev.status)?.display || ev.status,
          });
        });
      });

      if (rows.length === 0) {
        alert("No data available to export for the current filters.");
        return;
      }
      const worksheet = XLSX.utils.json_to_sheet(rows);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "AWS Alerts");
      const now = new Date();
      const dateStr = now.toLocaleDateString("en-CA");
      const filename = `aws_alerts_${dateStr}.xlsx`;
      XLSX.writeFile(workbook, filename);
    } catch (err) {
      console.error("Error exporting Excel:", err);
      alert("Failed to export Excel file.");
    }
  };

  return (
    <Box className="container-fluid main" sx={{ overflowX: "hidden", height: "100%" }}>
      {/* Header */}
      <Box className="d-flex align-items-center justify-content-between mb-3">
        <Box className="d-flex align-items-center mt-3">
          <Typography variant="h5" className="font-weight-bold me-2" sx={{ fontWeight: 900 }}>
            AWS ALERTS
          </Typography>
          {lastSyncedTime != null && (
            <Box className="d-flex align-items-center ml-3 text-s" sx={{ color: "#666" }}>
              <RefreshIcon sx={{ fontSize: 17, mr: 0.5 }} />
              {+lastSyncedTime < 60 || /min/i.test(String(lastSyncedTime))
                ? "Last synced just now"
                : `Last synced ${lastSyncedTime} ago`}
            </Box>
          )}
        </Box>

        {/* Controls row */}
        <Box
          className="d-flex gap-2 mt-3"
          sx={{ alignItems: "flex-end" }}
        >
          {awsProgress && awsProgress.total > 0 && awsProgress.loaded < awsProgress.total ? (
            <div className="badge badge-primary d-flex align-items-center mr-2">
              <p className="text-sm mb-0 ml-1">
                Fetching AWS Alerts ({awsProgress.loaded}/{awsProgress.total})...
              </p>
              <CircularProgress className="mx-2 text-white" size="1rem" />
            </div>
          ) : awsProgress && awsProgress.total > 0 && awsProgress.percentage === 100 ? (
            <div className="badge badge-success text-sm d-flex align-items-center mr-2">
              Data Loaded Successfully
              <Check className="ml-1 text-white" sx={{ fontSize: "1.2rem" }} />
            </div>
          ) : null}
          {/* Act By control */}
          <Box
            className="source-list ml-0"
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              position: "relative",
              width: "fit-content",
            }}
          >
            {/* Visible label pill */}
            <Button
              variant="contained"
              disableElevation
              className="mr-1"
              size="small"
              endIcon={<KeyboardArrowDown />}
              sx={{
                height: 28,
                px: 1.1,
                justifyContent: "space-between",
                bgcolor: "transparent",
                color: "#000",
                border: "1px solid #000",
                boxShadow: "none",
                textTransform: "none",
                fontSize: "11px",
                fontWeight: 500,
                lineHeight: 1,
                borderRadius: "4px",
                display: "flex",
                alignItems: "center",
                "&:hover": {
                  bgcolor: "transparent",
                  boxShadow: "none",
                  borderColor: "#000",
                },
                "& .MuiButton-endIcon > *:nth-of-type(1)": {
                  color: "#000",
                  fontSize: "14px",
                },
              }}
            >
              <span
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 4,
                  whiteSpace: "nowrap",
                }}
              >
                <span>Act By</span>
                <span style={{ color: "#555" }}>| {actByFilter}</span>
              </span>
            </Button>
            <Select
              id="act-by-filter"
              size="small"
              value={actByFilter}
              onChange={(e) => setActByFilter(e.target.value)}
              MenuProps={scopeMenuProps}
              sx={{
                position: "absolute",
                top: 0,
                left: 0,
                height: 28,
                width: "100%",
                opacity: 0,
                cursor: "pointer",
                "& .MuiOutlinedInput-notchedOutline": { border: "none" },
              }}
            >
              {ACT_BY_FILTER_OPTIONS.map((opt) => (
                <MenuItem key={opt} value={opt}>
                  <ListItemText primary={opt} />
                </MenuItem>
              ))}
            </Select>
          </Box>

          {/* Accounts control */}
          <Box className="source-list ml-2" sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Button
              variant="contained"
              disableElevation
              className="mr-1"
              size="small"
              endIcon={<KeyboardArrowDown />}
              sx={{ height: 34 }}
            >
              {accountView}
            </Button>
            <Select
              id="source"
              size="small"
              value={accountView}
              onChange={(e) => setAccountView(e.target.value)}
              sx={headerSelectSx}
            >
              <MenuItem value="My Accounts">
                <ListItemText primary="My Accounts" />
              </MenuItem>
              {SHOW_ALL && (
                <MenuItem value="All Accounts">
                  <ListItemText primary="All Accounts" />
                </MenuItem>
              )}
            </Select>
          </Box>
        </Box>
      </Box>

      <InfoBanner />
      {/* Search + Tabs */}
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2 }}>
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <TextField
            variant="standard"
            placeholder={`Search ${searchScope.toLowerCase()}`}
            sx={{
              maxWidth: "clamp(240px, 40vw, 380px)",
              mb: 0,
              backgroundColor: "transparent",
              "& .MuiInputBase-root": { color: "#000", fontSize: "14px" },
              "& .MuiInput-underline:before": { borderBottomColor: "#ccc" },
              "& .MuiInput-underline:hover:before": { borderBottomColor: "#999" },
              "& input::placeholder": { color: "#aaa", opacity: 1 },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" sx={{ color: "#bbb" }} />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end" sx={{ ml: 0.5, pr: 0 }}>
                  <Select
                    value={searchScope}
                    onChange={(e) => setSearchScope(e.target.value)}
                    size="small"
                    sx={{
                      ...scopeSelectSx,
                      pl: 0.5,
                      pr: 0.5,
                      "& .MuiSelect-select": { paddingRight: "26px !important" },
                    }}
                    MenuProps={scopeMenuProps}
                  >
                    <MenuItem value="Accounts">ACCOUNTS</MenuItem>
                    <MenuItem value="Events">EVENTS</MenuItem>
                  </Select>
                </InputAdornment>
              ),
            }}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          <IconButton
            onClick={handleExport}
            color="disabled"
            sx={{ ml: 1, position: "relative", top: "6px", p: 0.5 }}
            title="Export data to Excel"
          >
            <FileDownloadIcon />
          </IconButton>
        </Box>

        {SHOW_ALL && (
          <Box sx={{ display: "flex", alignItems: "center", mt: 1.5 }}>
            <Box
              sx={{
                position: "relative",
                display: "flex",
                alignItems: "center",
                border: "1px solid #e6e6e6ff",
                borderRadius: "8px 8px 0 0",
                bgcolor: "#fff",
                height: 34,
                overflow: "visible",
                width: "fit-content",
              }}
            >
              <Button
                onClick={() => setViewMode("active")}
                size="small"
                disableElevation
                disableRipple
                sx={{
                  minWidth: 0,
                  width: 120,
                  justifyContent: "center",
                  height: "100%",
                  textTransform: "none",
                  fontWeight: 700,
                  borderRadius: "8px 0 0 0",
                  color: viewMode === "active" ? "#fff" : "#000",
                  bgcolor: viewMode === "active" ? "#000" : "#fff",
                  position: "relative",
                  transition: "background-color 0s",
                  "&::after": {
                    content: '""',
                    position: "absolute",
                    top: "-0.4px",
                    right: "-18px",
                    width: "20px",
                    height: "102%",
                    borderBottomRightRadius: "6px",
                    backgroundColor: viewMode === "active" ? "#000" : "#fff",
                    transform: "skewX(-22deg)",
                    transformOrigin: "top left",
                    zIndex: 4,
                    pointerEvents: "none",
                    borderTop: "1px solid #e6e6e6ff",
                  },
                }}
              >
                Active Alerts
              </Button>
              <Button
                onClick={() => setViewMode("archived")}
                size="small"
                disableElevation
                disableRipple
                sx={{
                  minWidth: 0,
                  width: 138,
                  justifyContent: "center",
                  pl: 3.2,
                  pr: 2.2,
                  height: "100%",
                  textTransform: "none",
                  borderRadius: "0 8px 0 0",
                  color: viewMode === "archived" ? "#fff" : "#000",
                  fontWeight: 700,
                  bgcolor: viewMode === "archived" ? "#000" : "#fff",
                  position: "relative",
                }}
              >
                Archived Alerts
              </Button>
            </Box>
          </Box>
        )}
      </Box>

      {/* Table */}
      {hasEventsToShow ? (
        <Box
          className="mb-4"
          sx={{
            backgroundColor: "#fff",
            borderRadius: "4px",
            maxHeight: "65vh",
            overflowY: "auto",
          }}
        >
          <Box className="small ff-artifakt lh-1 ls-0">
            {/* Header */}
            <Box
              className="d-flex fst-normal text-uppercase font-weight-bold"
              sx={{
                position: "sticky",
                top: 0,
                backgroundColor: "#fff",
                boxShadow: "inset 0px -1px 0px #C7C5C5",
                py: 1,
                zIndex: 5,
                height: "36px",
                alignItems: "center",
              }}
            >
              {/* ACCOUNT NAME */}
              <Box
                role="columnheader"
                aria-sort={
                  acctSort.key === "name" ? (acctSort.dir === "asc" ? "ascending" : "descending") : "none"
                }
                tabIndex={0}
                sx={{ flex: "1 1 26%", pl: 6, minWidth: 0 }}
                onClick={() => handleHeaderClick("name")}
                onKeyDown={(e) => handleHeaderKeyDown(e, "name")}
              >
                <SortLabel active={acctSort.key === "name"} dir={acctSort.dir}>
                  ACCOUNT NAME
                </SortLabel>
              </Box>

              {/* ACCOUNT ID */}
              <Box
                role="columnheader"
                aria-sort={
                  acctSort.key === "id" ? (acctSort.dir === "asc" ? "ascending" : "descending") : "none"
                }
                tabIndex={0}
                sx={{ flex: "1 1 18%", minWidth: 0 }}
                onClick={() => handleHeaderClick("id")}
                onKeyDown={(e) => handleHeaderKeyDown(e, "id")}
              >
                <SortLabel active={acctSort.key === "id"} dir={acctSort.dir}>
                  ACCOUNT ID
                </SortLabel>
              </Box>

              {/* ENVIRONMENT */}
              <Box
                role="columnheader"
                aria-sort={
                  acctSort.key === "env" ? (acctSort.dir === "asc" ? "ascending" : "descending") : "none"
                }
                tabIndex={0}
                sx={{ flex: "1 1 15%", minWidth: 0 }}
                onClick={() => handleHeaderClick("env")}
                onKeyDown={(e) => handleHeaderKeyDown(e, "env")}
              >
                <SortLabel active={acctSort.key === "env"} dir={acctSort.dir}>
                  ENVIRONMENT
                </SortLabel>
              </Box>

              {/* BUSINESS SERVICES (not sortable) */}
              <Box sx={{ flex: "1 1 26%", minWidth: 0 }}>
                BUSINESS SERVICES
              </Box>

              {/* TOTAL EVENTS */}
              <Box
                role="columnheader"
                aria-sort={
                  acctSort.key === "total" ? (acctSort.dir === "asc" ? "ascending" : "descending") : "none"
                }
                tabIndex={0}
                className="text-end pe-4"
                sx={{ flex: "1 1 15%", minWidth: 0 }}
                onClick={() => handleHeaderClick("total")}
                onKeyDown={(e) => handleHeaderKeyDown(e, "total")}
              >
                <SortLabel active={acctSort.key === "total"} dir={acctSort.dir}>
                  TOTAL EVENTS
                </SortLabel>
              </Box>
            </Box>

            {/* Rows */}
            {currentPageAccounts.map((account, index) => {
              const canExpand = filteredEventCountForAccount(account.id) > 0;

              return (
                <React.Fragment key={index}>
                  <Box
                    key={`${account.id}-${index}`}
                    className="d-flex align-items-center py-1 text-lite"
                    sx={{
                      position: "relative",
                      borderBottom: "1px solid #C7C5C5",
                      py: 1,
                      height: "30px",
                      textTransform: "none",
                      backgroundColor:
                        expandedAccount === account.id ? " #0000001A" : "transparent",
                      ...(expandedAccount === account.id && {
                        "&::before": {
                          content: '""',
                          position: "absolute",
                          left: 0,
                          top: 0,
                          bottom: 0,
                          width: "4px",
                          backgroundColor: "#000",
                        },
                      }),
                    }}
                  >
                    <Box
                      sx={{ flex: "1 1 26%", pl: 2, minWidth: 0 }}
                      className="d-flex align-items-center text-nowrap"
                    >
                      <IconButton
                        size="small"
                        disabled={!canExpand}
                        sx={{ p: 0, pr: 0 }}
                        onClick={() => {
                          if (!canExpand) return;
                          if (currentAccountDetails[account.id]) {
                            const newExpandedAccount =
                              expandedAccount === account.id ? null : account.id;
                            setExpandedAccount(newExpandedAccount);
                            if (newExpandedAccount === account.id) {
                              const regions = getRegionsForAccount(account.id, { matchQuery: true });
                              setSelectedRegion(regions.length > 0 ? regions[0] : null);
                            } else {
                              setSelectedRegion(null);
                            }
                          }
                        }}
                      >
                        <ArrowForwardIosIcon
                          fontSize="small"
                          className="text-s"
                          sx={{
                            opacity: canExpand ? 1 : 0.35,
                            transform:
                              expandedAccount === account.id ? "rotate(90deg)" : "rotate(0deg)",
                            transition: "transform 0.2s ease",
                          }}
                        />
                      </IconButton>
                      <Typography
                        className="fw-semibold fst-normal text-lowercase text-truncate text-s lh-1"
                        sx={{ paddingLeft: 2.5 }}
                        title={account.name}
                      >
                        {account.name}
                      </Typography>
                    </Box>

                    <Box sx={{ flex: "1 1 18%", minWidth: 0 }}>{account.id}</Box>
                    <Box sx={{ flex: "1 1 15%", minWidth: 0 }}>{account.env}</Box>
                    <Box className="pr-5" sx={{ flex: "1 1 26%", minWidth: 0 }}>
                      {(account.services || []).map((svc, i) => (
                        <Chip
                          key={i}
                          label={svc}
                          size="small"
                          className="text-white me-1 text-xs"
                          sx={{
                            height: "16px",
                            padding: "0 4px",
                            backgroundColor: "#007BA7",
                            borderRadius: "5px",
                            ".MuiChip-label": { padding: 0 },
                          }}
                        />
                      ))}
                    </Box>

                    <Box sx={{ flex: "1 1 15%", minWidth: 0 }} className="text-end pe-4">
                      <Chip
                        label={filteredEventCountForAccount(account.id)}
                        size="small"
                        className="text-white font-weight-bold text-center px-1 me-1 text-xs"
                        sx={{
                          backgroundColor: "#FF6B6B",
                          height: "18px",
                          minWidth: "22px",
                          borderRadius: "10px",
                          ".MuiChip-label": { padding: 0 },
                        }}
                      />
                    </Box>
                  </Box>

                  {canExpand &&
                    account.id === expandedAccount &&
                    currentAccountDetails[account.id] &&
                    (() => {
                      const regions = getRegionsForAccount(account.id, { matchQuery: true });
                      return regions.length > 0 ? (
                        <Box
                          className="account-details-container mx-4 mb-4"
                          sx={{
                            backgroundColor: "#fff",
                            borderRadius: "4px",
                            px: 2.75,
                            position: "relative",
                            zIndex: 1,
                          }}
                        >
                          <Box
                            className="d-flex align-items-end"
                            sx={{
                              height: "36px",
                              gap: 2,
                              borderBottom: "1px solid #C7C5C5",
                            }}
                          >
                            {regions.map((region) => (
                              <Button
                                key={region}
                                className={`btn-sm text-s ${
                                  selectedRegion === region
                                    ? "font-weight-bold text-body-color"
                                    : ""
                                }`}
                                variant="text"
                                onClick={() => setSelectedRegion(region)}
                                sx={{
                                  minWidth: "auto",
                                  px: 1,
                                  mb: "-1px",
                                  textTransform: "none",
                                  color: selectedRegion === region ? "#0a0a0aff" : "#666",
                                  backgroundColor: "transparent",
                                  borderRadius: "0",
                                  borderBottom:
                                    selectedRegion === region
                                      ? "2.5px solid #0a0a0aff"
                                      : "2.5px solid transparent",
                                  "&:hover": {
                                    backgroundColor: "transparent",
                                    color: "#000000ff",
                                    borderBottom: "2.5px solid #C7C5C5",
                                  },
                                }}
                              >
                                {region}
                              </Button>
                            ))}
                          </Box>

                          {/* Account Details header */}
                          <Box
                            className="d-flex fst-normal font-weight-bold text-uppercase sticky-top"
                            sx={{
                              backgroundColor: "#fff",
                              boxShadow: "inset 0px -1px 0px #C7C5C5",
                              height: "36px",
                              alignItems: "center",
                              top: 0,
                              zIndex: 5,
                            }}
                          >
                            <Box sx={{ flex: "0 0 8%", pl: 1 }}>EVENT ID</Box>
                            <Box sx={{ flex: "0 0 20%" }}>TITLE</Box>
                            <Box sx={{ flex: "0 0 10%", pl: 2 }}>IMPACT</Box>
                            <Box sx={{ flex: "1 1 30%", pl: 3 }}>ACTION REQUIRED</Box>
                            <Box sx={{ flex: "0 0 10%", pl: 2 }}>START DATE</Box>
                            <Box sx={{ flex: "0 0 10%", pl: 2 }}>ACT BY</Box>
                            <Box sx={{ flex: "0 0 12%" }}>
                              STATUS
                            </Box>
                            {!isArchivedView && <Box sx={{ flex: "0 0 6%" }}>ACTIONS</Box>}
                          </Box>

                          {/* Events */}
                          {expandedEvents.map((item, idx) => {
                            const effectiveStatus =
                              statusOverrides[item.eventId] || item.status;
                            const acks = parseAck(item.ack);
                            const lastAck = acks.length
                              ? acks[acks.length - 1]
                              : null;
                            const isAck =
                              effectiveStatus === "ACKNOWLEDGED" || acks.length > 0;
                            const statusMeta = getStatus(
                              isAck ? "ACKNOWLEDGED" : effectiveStatus
                            );
                            const metaFromState = ackMetaByEvent[item.eventId];
                            const meta =
                              metaFromState ||
                              (lastAck
                                ? {
                                    user: lastAck.user || lastAck.username || "",
                                    comment: lastAck.comment || "",
                                    date:
                                      lastAck.date ||
                                      lastAck.datetime ||
                                      lastAck.timestamp ||
                                      "",
                                  }
                                : undefined);

                            const prettyDate =
                              meta && meta.date
                                ? moment(meta.date).isValid()
                                  ? moment(meta.date).format("DD MMM YYYY")
                                  : meta.date
                                : "";

                            const tooltip =
                              isAck && meta ? (
                                <Box sx={{ fontSize: 12, lineHeight: 1.3 }}>
                                  By <b>{meta.user || "—"}</b>
                                  {prettyDate ? (
                                    <>
                                      {" "}
                                      on <b>{prettyDate}</b>.
                                    </>
                                  ) : (
                                    "."
                                  )}
                                  {meta.comment ? (
                                    <>
                                      <br />
                                      <b>Comment</b> : {meta.comment}
                                    </>
                                  ) : null}
                                </Box>
                              ) : (
                                "Acknowledged"
                              );

                            return (
                              <Box
                                key={idx}
                                className="d-flex align-items-center py-1 event-row cursor-pointer text-s"
                                sx={{
                                  borderBottom: "1px solid #C7C5C5",
                                  py: 1,
                                  height: "30px",
                                  textTransform: "none",
                                  "&:hover": { backgroundColor: "#f5f5f5" },
                                }}
                                onClick={() => openRelease(item.eventId)}
                              >
                                <Box className="event-id-cell" sx={{ flex: "0 0 8%", pl: 1 }}>
                                  <Typography className="fw-semibold fst-normal text-truncate event-id-text text-s">
                                    {item.eventId}
                                  </Typography>
                                </Box>
                                <Box className="title-cell text-truncate" sx={{ flex: "0 0 20%" }}>
                                  {cleanTitle(item.title)}
                                </Box>
                                <Box className="impact-cell d-flex align-items-center" sx={{ flex: "0 0 10%", pl: 2 }}>
                                  <div className="d-flex align-items-center">
                                    <StyledRating
                                      name="impact-rating"
                                      value={impactScore(item.impact)}
                                      max={3}
                                      readOnly
                                      icon={<Square sx={{ fontSize: 12 }} />}
                                      emptyIcon={<Square sx={{ fontSize: 12 }} />}
                                    />
                                    <span className="ml-1 text-s">{item.impact}</span>
                                  </div>
                                </Box>
                                <Box className="text-truncate" sx={{ flex: "1 1 30%", pr: 2, pl: 3 }}>
                                  <Tooltip
                                    title={item.actionRequired}
                                    position="bottom"
                                    sx={{
                                      "& .MuiTooltip-tooltip": {
                                        fontSize: "18px",
                                        maxWidth: "500px",
                                        backgroundColor: "#aaa9a9ff",
                                        color: "#fff",
                                      },
                                    }}
                                  >
                                    <Box
                                      sx={{
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                        whiteSpace: "nowrap",
                                        width: "100%",
                                        lineHeight: "1.4",
                                      }}
                                    >
                                      {item.actionRequired}
                                    </Box>
                                  </Tooltip>
                                </Box>
                                <Box className="date-cell" sx={{ flex: "0 0 10%", pl: 2 }}>
                                  {fmt(item.planned_start_date)}
                                </Box>
                                <Box className="date-cell" sx={{ flex: "0 0 10%", pl: 2 }}>
                                  {fmt(item.act_by)}
                                </Box>

                                {/* STATUS */}
                                <Box
                                  className="status-cell"
                                  sx={{
                                    flex: "0 0 12%",
                                    minWidth: 0,
                                    overflow: "hidden",
                                    whiteSpace: "nowrap",
                                    display: "flex",
                                    alignItems: "center",
                                  }}
                                >
                                  {(() => {
                                    const { show, completed, total } = getImpactProgress(item);

                                    return (
                                      <Box
                                        sx={{
                                          display: "flex",
                                          alignItems: "center",
                                          gap: "2px",
                                          width: "100%",
                                          minWidth: 0,
                                        }}
                                      >
                                        <Box
                                          sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: "2px",
                                            minWidth: 0,
                                            overflow: "hidden",
                                          }}
                                        >
                                          <span
                                            style={{
                                              fontWeight: isAck ? 600 : 400,
                                              color: statusMeta.color,
                                              overflow: "hidden",
                                              textOverflow: "ellipsis",
                                              whiteSpace: "nowrap",
                                              minWidth: 0,
                                            }}
                                          >
                                            {statusMeta.display}
                                          </span>

                                          {isAck ? (
                                            <Tooltip title={tooltip} arrow>
                                              <InfoOutlinedIcon
                                                sx={{
                                                  fontSize: 16,
                                                  color: statusMeta.color,
                                                  flex: "0 0 auto",
                                                }}
                                              />
                                            </Tooltip>
                                          ) : (
                                            show &&
                                            statusMeta.display === "Pending" && (
                                            <Tooltip title="Impacted Resource Completion Status" arrow>
                                              <Chip
                                                size="small"
                                                label={`${completed}/${total}`}
                                                className="ml-1 flex-shrink-0 px-1 fw-bold rounded-pill bg-gray text-dark text-xs chip-h-14"
                                              />
                                            </Tooltip>
                                            )
                                          )}
                                        </Box>
                                      </Box>
                                    );
                                  })()}
                                </Box>
                                {/* ACTIONS (hidden on archived) */}
                                {!isArchivedView && (
                                  <Box
                                    className="action-cell"
                                    sx={{ flex: "0 0 6%", display: "flex", justifyContent: "flex-start" }}
                                    onClick={(e) => e.stopPropagation()}
                                  >
                                    {!isArchivedView && !isAck ? (
                                      <IconButton
                                        size="small"
                                        onClick={(e) => openActionMenu(e, item.eventId)}
                                        sx={{
                                          p: 0.25,
                                          ml: -0.5,
                                          color: "#6B6B6B",
                                          "&:hover": { bgcolor: "transparent", color: "#000" },
                                        }}
                                        aria-label="actions"
                                        aria-haspopup="true"
                                        aria-controls={actionAnchorEl ? "event-action-menu" : undefined}
                                        aria-expanded={
                                          Boolean(actionAnchorEl && actionMenuEventId === item.eventId) ? "true" : undefined
                                        }
                                      >
                                        <MoreHorizIcon sx={{ fontSize: 24 }} />
                                      </IconButton>
                                    ) : null}
                                  </Box>
                                )}
                              </Box>
                            );
                          })}
                        </Box>
                      ) : null;
                    })()}
                </React.Fragment>
              );
            })}
          </Box>
        </Box>
      ) : (
        <Box
          className="mb-4"
          sx={{
            backgroundColor: "#fff",
            borderRadius: "4px",
            maxHeight: "65vh",
            overflowY: "auto",
            p: 4,
            textAlign: "center",
            my: 2,
          }}
        >
          <CloudOffOutlined sx={{ fontSize: 48, color: "#9e9e9e", mb: 1 }} />
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>
            {viewMode === "archived"
              ? "No archived AWS events to show."
              : "No AWS events to show."}
          </Typography>
        </Box>
      )}

      {hasEventsToShow && (
        <Paginator
          totalItems={sortedAccounts.length}
          page={page}
          rowsPerPage={rowsPerPage}
          rowsPerPageOptions={ROWS_PER_PAGE_OPTS}
          onPageChange={(v) => setPage(v)}
          onRowsPerPageChange={(opt) => {
            setRowsPerPage(opt);
            setPage(1);
          }}
        />
      )}
      {/* Actions menu (not rendered on archived view) */}
      {!isArchivedView && (
        <Menu
          id="event-action-menu"
          anchorEl={actionAnchorEl}
          open={Boolean(actionAnchorEl)}
          onClose={closeActionMenu}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          transformOrigin={{ vertical: "top", horizontal: "right" }}
          MenuListProps={{ dense: true }}
        >
          {["Acknowledgement"].map((opt) => {
            const eff =
              (actionMenuEventId &&
                (statusOverrides[actionMenuEventId] ??
                  localEventIndex[actionMenuEventId]?.status)) ||
              "";
            const hasAck =
              actionMenuEventId &&
              parseAck(localEventIndex[actionMenuEventId]?.ack).length > 0;

            return (
              <MenuItem
                key={opt}
                disabled={eff === "ACKNOWLEDGED" || hasAck}
                selected={
                  Boolean(actionMenuEventId) &&
                  actionSelections[actionMenuEventId] === opt
                }
                onClick={() => selectAction(opt)}
              >
                {opt}
              </MenuItem>
            );
          })}
        </Menu>
      )}

      {/* Actions menu (not rendered on archived view) */}
      {!isArchivedView && (
        <Menu
          id="event-action-menu"
          anchorEl={actionAnchorEl}
          open={Boolean(actionAnchorEl)}
          onClose={closeActionMenu}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          transformOrigin={{ vertical: "top", horizontal: "right" }}
          MenuListProps={{ dense: true }}
        >
          {["Acknowledgement"].map((opt) => {
            const eff =
              (actionMenuEventId &&
                (statusOverrides[actionMenuEventId] ??
                  localEventIndex[actionMenuEventId]?.status)) ||
              "";
            const hasAck =
              actionMenuEventId &&
              parseAck(localEventIndex[actionMenuEventId]?.ack).length > 0;

            return (
              <MenuItem
                key={opt}
                disabled={eff === "ACKNOWLEDGED" || hasAck}
                selected={
                  Boolean(actionMenuEventId) &&
                  actionSelections[actionMenuEventId] === opt
                }
                onClick={() => selectAction(opt)}
              >
                {opt}
              </MenuItem>
            );
          })}
        </Menu>
      )}

      {/* Actions menu (not rendered on archived view) */}
      {!isArchivedView && (
        <Menu
          id="event-action-menu"
          anchorEl={actionAnchorEl}
          open={Boolean(actionAnchorEl)}
          onClose={closeActionMenu}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          transformOrigin={{ vertical: "top", horizontal: "right" }}
          MenuListProps={{ dense: true }}
        >
          {["Acknowledgement"].map((opt) => {
            const eff =
              (actionMenuEventId &&
                (statusOverrides[actionMenuEventId] ??
                  localEventIndex[actionMenuEventId]?.status)) ||
              "";
            const hasAck =
              actionMenuEventId &&
              parseAck(localEventIndex[actionMenuEventId]?.ack).length > 0;

            return (
              <MenuItem
                key={opt}
                disabled={eff === "ACKNOWLEDGED" || hasAck}
                selected={
                  Boolean(actionMenuEventId) &&
                  actionSelections[actionMenuEventId] === opt
                }
                onClick={() => selectAction(opt)}
              >
                {opt}
              </MenuItem>
            );
          })}
        </Menu>
      )}

      {/* Acknowledge dialog */}
      <AcknowledgeDialog
        open={ackOpen}
        eventId={ackEventId}
        defaultComment={ackComment}
        username={username}
        onClose={handleAckClose}
        onSuccess={handleAckSuccess}
      />

      {/* Details drawer */}
      <AwsEventDetails
        isOpen={isModalOpen}
        onClose={closeRelease}
        event={eventData}
        loading={loading}
        acknowledged={eventData?.acknowledged}
        ackMeta={eventData?.ackMeta}
      />

      {/* Unauthorized dialog */}
      <Dialog
        open={unauthorizedOpen}
        onClose={() => setUnauthorizedOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 1, p: 0 } }}
      >
        <DialogTitle className="position-relative text-center fw-bold" sx={{ fontSize: "17px", py: 1.5 }}>
          Access Denied
          <IconButton
            aria-label="close"
            onClick={() => setUnauthorizedOpen(false)}
            className="position-absolute"
            sx={{
              right: 8,
              top: 8,
              color: "#888",
              "&:hover": { color: "#000" },
            }}
          >
            <CloseIcon sx={{ fontSize: 20 }} />
          </IconButton>
        </DialogTitle>
        <DialogContent className="text-center text-muted" sx={{ px: 3, py: 2, fontSize: "14px", lineHeight: 1.5 }}>
          Sorry, you are unauthorized to view AWS events for this specific account.
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default AwsAlerts;
